// src/utils/simulationYears.js
//
// The set of years a feasibility study can be run for, in one place.
//
// This list was previously computed inline on the Simulation page. The Weather
// page now offers the same choice, so inspecting the platform's weather for a
// year the study cannot actually be run for would be a quiet trap: the charts
// would describe a year no run will ever use. Sharing the list makes the two
// pages impossible to drift apart.
//
// The window ends at last year because the current year is incomplete — the
// weather archive lags real time by several days, so a run for it would stop
// short of 31 December.

/** Last complete calendar year — the default the whole app falls back to. */
export function defaultSimulationYear(now = new Date()) {
  return now.getFullYear() - 1;
}

/**
 * Selectable simulation years, oldest first.
 * Five years ending at the last complete one.
 */
export function simulationYears(now = new Date()) {
  const last = defaultSimulationYear(now);
  const years = [];
  for (let i = 4; i >= 0; i -= 1) years.push(last - i);
  return years;
}

/**
 * Coerce a stored value to a selectable year.
 *
 * A project saved years ago can carry a year that has since fallen out of the
 * window; returning the default rather than the stale value keeps the select
 * from showing a blank when its value matches no option.
 */
export function normalizeSimulationYear(value, now = new Date()) {
  const year = Number(value);
  return simulationYears(now).includes(year) ? year : defaultSimulationYear(now);
}

/**
 * Resolve the operating calendar separately from the reference data year.
 * Legacy projects keep their previous start until the user changes it.
 */
export function operatingStartYear(config) {
  const value = config?.feasibility?.operatingStartYear;
  if (value !== undefined && value !== null) return Number(value);
  return Number(config?.feasibility?.simulationYear) || defaultSimulationYear();
}

export function validOperatingStartYear(value) {
  const year = Number(value);
  return Number.isInteger(year) && year >= 1900 && year <= 2200;
}

// Materialize the legacy start before the reference year can be edited.
export function normalizeProjectYears(config) {
  return { ...config, feasibility: { ...config?.feasibility,
    operatingStartYear: operatingStartYear(config) } };
}

export function projectLifetimeDates(startYear, lifetimeYears) {
  const year = Number(startYear);
  const lifetime = Math.trunc(Number(lifetimeYears));
  if (!Number.isInteger(year) || !Number.isFinite(lifetime) || lifetime < 1) {
    return { start: null, end: null };
  }
  return {
    start: new Date(year, 0, 1),
    end: new Date(year + lifetime - 1, 11, 31),
  };
}
